import React, { Suspense, useMemo, useContext } from 'react';
import { useParams, useSearchParams, Navigate } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  alpha,
  Tooltip,
  keyframes,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { getSectionById, SectionTab } from './sectionConfig';
import { useB2BAccess } from '../../hooks/useModuleAccess';
import { useTierAccess } from '../../hooks/useTierAccess';
import { useMenuLayout } from '../../contexts/MenuLayoutContext';
import { ColorModeContext } from '../../contexts/ColorModeContext';
import { useAnimations } from '../../contexts/AnimationsContext';
import { palette, typography, theme as themeConfig } from '../../theme/colors';

// Keyframe animations
const glowPulse = keyframes`
  0%, 100% { opacity: 1; box-shadow: 0 0 8px currentColor; }
  50% { opacity: 0.7; box-shadow: 0 0 16px currentColor; }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;


interface TabPanelProps {
  children?: React.ReactNode;
  value: string;
  selectedTab: string;
  animationsEnabled: boolean;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, selectedTab, animationsEnabled }) => {
  return (
    <Box
      role="tabpanel"
      hidden={value !== selectedTab}
      id={`section-tabpanel-${value}`}
      aria-labelledby={`section-tab-${value}`}
      sx={{
        flexGrow: 1,
        display: value === selectedTab ? 'flex' : 'none',
        flexDirection: 'column',
        ...(animationsEnabled && value === selectedTab ? {
          animation: `${slideUp} 0.3s ease-out`,
        } : {}),
      }}
    >
      {value === selectedTab && children}
    </Box>
  );
};

const LoadingFallback: React.FC = () => {
  const { mode } = useContext(ColorModeContext);
  const { animationsEnabled } = useAnimations();
  const colors = mode === 'dark' ? {
    bg: palette.background,
    text: palette.textMuted,
  } : {
    bg: palette.light.background,
    text: palette.light.textMuted,
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        minHeight: 300,
        gap: 2,
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: 48,
          height: 48,
        }}
      >
        <CircularProgress
          size={48}
          thickness={2}
          sx={{
            color: palette.primary,
            '& .MuiCircularProgress-circle': {
              strokeLinecap: 'square',
            },
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            inset: 8,
            border: `1px solid ${alpha(palette.primary, 0.3)}`,
            ...(animationsEnabled ? {
              animation: `${glowPulse} 1.5s ease-in-out infinite`,
            } : {}),
            color: palette.primary,
          }}
        />
      </Box>
      <Typography
        sx={{
          fontFamily: typography.fontFamily.mono,
          fontSize: '10px',
          color: colors.text,
          letterSpacing: '0.15em',
          textTransform: 'uppercase',
        }}
      >
        LOADING MODULE...
      </Typography>
    </Box>
  );
};

export const SectionPage: React.FC = () => {
  const { sectionId } = useParams<{ sectionId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { mode } = useContext(ColorModeContext);
  const { layout } = useMenuLayout();
  const { animationsEnabled } = useAnimations();
  const { hasAccess: hasB2BAccess } = useB2BAccess();
  const { hasTierAccess } = useTierAccess();

  const colors = mode === 'dark' ? {
    bg: palette.background,
    surface: palette.surface,
    surfaceElevated: palette.surfaceElevated,
    border: palette.border,
    text: palette.textPrimary,
    textSecondary: palette.textSecondary,
    textMuted: palette.textMuted,
  } : {
    bg: palette.light.background,
    surface: palette.light.surface,
    surfaceElevated: palette.light.surfaceElevated,
    border: palette.light.border,
    text: palette.light.textPrimary,
    textSecondary: palette.light.textSecondary,
    textMuted: palette.light.textMuted,
  };

  const section = useMemo(() => {
    if (!sectionId) return undefined;
    return getSectionById(sectionId);
  }, [sectionId]);

  // Filter tabs based on B2B access and tier access
  const visibleTabs = useMemo(() => {
    if (!section) return [];
    return section.tabs.filter((tab) => {
      if (tab.requiresB2B && !hasB2BAccess) {
        return false;
      }
      return true;
    });
  }, [section, hasB2BAccess]);

  const currentTabId = searchParams.get('tab') || (visibleTabs.length > 0 ? visibleTabs[0].id : '');
  const currentTabIndex = visibleTabs.findIndex((t) => t.id === currentTabId);

  // If in flat mode, redirect to the appropriate flat route
  if (layout === 'flat' && section && visibleTabs.length > 0) {
    const tab = visibleTabs.find((t) => t.id === currentTabId) || visibleTabs[0];
    return <Navigate to={tab.flatRoute} replace />;
  }

  if (!section) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" color="error">
          Section not found
        </Typography>
      </Box>
    );
  }

  if (visibleTabs.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" color="text.secondary">
          No accessible tabs in this section
        </Typography>
      </Box>
    );
  }

  const handleTabClick = (tabId: string) => {
    setSearchParams({ tab: tabId });
  };

  const isTabLocked = (tab: SectionTab): boolean => {
    if (tab.requiredTier && !hasTierAccess(tab.requiredTier)) {
      return true;
    }
    return false;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Section Header - Command Center Style */}
      <Box
        sx={{
          px: 3,
          pt: 2,
          pb: 0,
          position: 'relative',
          background: `linear-gradient(180deg, ${alpha(colors.surface, 0.95)} 0%, ${alpha(colors.bg, 0.98)} 100%)`,
          borderBottom: `1px solid ${alpha(palette.primary, 0.15)}`,
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '1px',
            background: `linear-gradient(90deg, transparent 0%, ${alpha(palette.primary, 0.4)} 50%, transparent 100%)`,
          },
        }}
      >
        {/* Section Title Row */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2.5,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* Section Badge */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.5,
                py: 0.5,
                border: `1px solid ${alpha(palette.primary, 0.3)}`,
                background: alpha(palette.primary, 0.08),
                ...(animationsEnabled ? {
                  animation: `${slideUp} 0.4s ease-out`,
                } : {}),
              }}
            >
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  backgroundColor: palette.primary,
                  boxShadow: `0 0 8px ${palette.primary}`,
                  ...(animationsEnabled ? {
                    animation: `${glowPulse} 2s ease-in-out infinite`,
                  } : {}),
                }}
              />
              <Typography
                sx={{
                  fontFamily: typography.fontFamily.mono,
                  fontSize: '10px',
                  color: palette.primary,
                  letterSpacing: '0.15em',
                  textTransform: 'uppercase',
                  fontWeight: typography.fontWeight.bold,
                }}
              >
                MODULE_{sectionId?.toUpperCase().slice(0, 4)}
              </Typography>
            </Box>

            {/* Section Title */}
            <Typography
              sx={{
                fontFamily: typography.fontFamily.mono,
                fontWeight: typography.fontWeight.bold,
                color: colors.text,
                fontSize: '18px',
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
                ...(animationsEnabled ? {
                  animation: `${slideUp} 0.4s ease-out 0.1s both`,
                } : {}),
              }}
            >
              {section.label}
            </Typography>
          </Box>

          {/* Tab Counter */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              ...(animationsEnabled ? {
                animation: `${slideUp} 0.4s ease-out 0.2s both`,
              } : {}),
            }}
          >
            <Typography
              sx={{
                fontFamily: typography.fontFamily.mono,
                fontSize: '10px',
                color: colors.textMuted,
                letterSpacing: '0.1em',
              }}
            >
              TAB {currentTabIndex + 1} / {visibleTabs.length}
            </Typography>
          </Box>
        </Box>

        {/* HUD-Style Tabs */}
        <Box
          sx={{
            display: 'flex',
            gap: 0.5,
            pb: 0,
            position: 'relative',
            overflowX: 'auto',
            '&::-webkit-scrollbar': {
              height: 2,
            },
            '&::-webkit-scrollbar-thumb': {
              background: alpha(palette.primary, 0.3),
            },
          }}
          role="tablist"
        >
          {visibleTabs.map((tab, index) => {
            const isActive = tab.id === currentTabId;
            const locked = isTabLocked(tab);
            const tabNumber = String(index + 1).padStart(2, '0');

            return (
              <Box
                key={tab.id}
                role="tab"
                aria-selected={isActive}
                aria-controls={`section-tabpanel-${tab.id}`}
                id={`section-tab-${tab.id}`}
                tabIndex={locked ? -1 : 0}
                onClick={() => !locked && handleTabClick(tab.id)}
                onKeyDown={(e) => {
                  if ((e.key === 'Enter' || e.key === ' ') && !locked) {
                    handleTabClick(tab.id);
                  }
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 2,
                  py: 1.25,
                  cursor: locked ? 'not-allowed' : 'pointer',
                  position: 'relative',
                  transition: `all ${themeConfig.transitions.fast}`,
                  opacity: locked ? 0.4 : 1,
                  ...(animationsEnabled ? {
                    animation: `${slideUp} 0.3s ease-out ${index * 0.05}s both`,
                  } : {}),
                  background: isActive
                    ? `linear-gradient(180deg, ${alpha(palette.primary, 0.12)} 0%, ${alpha(palette.primary, 0.05)} 100%)`
                    : 'transparent',
                  borderTop: isActive ? `2px solid ${palette.primary}` : `2px solid transparent`,
                  borderLeft: `1px solid ${isActive ? alpha(palette.primary, 0.3) : 'transparent'}`,
                  borderRight: `1px solid ${isActive ? alpha(palette.primary, 0.3) : 'transparent'}`,
                  // Glow effect for active tab
                  '&::before': isActive ? {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: '20%',
                    right: '20%',
                    height: '2px',
                    background: palette.primary,
                    boxShadow: `0 0 12px ${palette.primary}, 0 0 24px ${alpha(palette.primary, 0.5)}`,
                  } : {},
                  // Bottom connector line
                  '&::after': isActive ? {
                    content: '""',
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: '1px',
                    background: colors.bg,
                  } : {},
                  '&:hover': !locked ? {
                    background: isActive
                      ? `linear-gradient(180deg, ${alpha(palette.primary, 0.15)} 0%, ${alpha(palette.primary, 0.08)} 100%)`
                      : alpha(palette.primary, 0.05),
                    '& .tab-number': {
                      color: palette.primary,
                    },
                  } : {},
                  '&:focus-visible': {
                    outline: `2px solid ${palette.primary}`,
                    outlineOffset: '-2px',
                  },
                }}
              >
                {/* Tab Number */}
                <Typography
                  className="tab-number"
                  sx={{
                    fontFamily: typography.fontFamily.mono,
                    fontSize: '9px',
                    color: isActive ? palette.primary : alpha(colors.textMuted, 0.5),
                    letterSpacing: '0.1em',
                    fontWeight: typography.fontWeight.bold,
                    transition: `all ${themeConfig.transitions.fast}`,
                  }}
                >
                  {tabNumber}
                </Typography>

                {/* Tab Icon */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: locked
                      ? colors.textMuted
                      : isActive
                        ? palette.primary
                        : colors.textSecondary,
                    transition: `all ${themeConfig.transitions.fast}`,
                    '& svg': {
                      fontSize: 16,
                      filter: isActive ? `drop-shadow(0 0 4px ${palette.primary})` : 'none',
                    },
                  }}
                >
                  {locked ? (
                    <Tooltip title="Upgrade to unlock" arrow>
                      <LockOutlinedIcon sx={{ fontSize: 14 }} />
                    </Tooltip>
                  ) : (
                    tab.icon
                  )}
                </Box>

                {/* Tab Label */}
                <Typography
                  sx={{
                    fontFamily: typography.fontFamily.mono,
                    fontSize: '11px',
                    fontWeight: isActive ? typography.fontWeight.bold : typography.fontWeight.medium,
                    color: locked
                      ? colors.textMuted
                      : isActive
                        ? palette.primary
                        : colors.textSecondary,
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase',
                    transition: `all ${themeConfig.transitions.fast}`,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {tab.label}
                </Typography>

                {/* Active Status Dot */}
                {isActive && (
                  <Box
                    sx={{
                      width: 4,
                      height: 4,
                      borderRadius: '50%',
                      backgroundColor: palette.success,
                      boxShadow: `0 0 6px ${palette.success}`,
                      ml: 0.5,
                    }}
                  />
                )}
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* Tab Content */}
      <Box
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'auto',
          position: 'relative',
          // Subtle top shadow/gradient
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 8,
            background: `linear-gradient(180deg, ${alpha(palette.primary, 0.03)} 0%, transparent 100%)`,
            pointerEvents: 'none',
            zIndex: 1,
          },
        }}
      >
        {visibleTabs.map((tab) => (
          <TabPanel key={tab.id} value={tab.id} selectedTab={currentTabId} animationsEnabled={animationsEnabled}>
            <Suspense fallback={<LoadingFallback />}>
              <tab.component />
            </Suspense>
          </TabPanel>
        ))}
      </Box>
    </Box>
  );
};
